import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../images/white_logo.png";
import { useTenant } from "../context/TenantContext";
import { useLabels } from "../utils/labels";
import {
  MdAbc,
  MdBusiness,
  MdCalendarMonth,
  MdCategory,
  MdClose,
  MdCopyAll,
  MdDiscount,
  MdHome,
  MdLineAxis,
  MdOutlineDashboardCustomize,
  MdOutlineMiscellaneousServices,
  MdPayment,
  MdPayments,
  MdPeople,
  MdPerson,
  MdQrCode,
  MdSettings,
  MdPolicy,
  MdShare,
  MdReport,
  MdMenu,
  MdAccountCircle,
  MdLogout,
  MdExpandMore,
  MdNotifications,
  MdInventory2,
} from "react-icons/md";
import QRCode from "react-qr-code";
import CopyToClipboard from "react-copy-to-clipboard";
import { RWebShare } from "react-web-share";
import LeadsComponent from "./leadsComponent";
import SettingsSidebar from "./SettingsSidebar";
import { successMessage } from "../logic/error/action";
import { logOut } from "../logic/login/action";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Typography,
  Badge,
  Avatar,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

const TopBar = () => {
  const selectorAuth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const nav = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const labels = useLabels();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [openLeads, setOpenLeads] = useState(false);
  const [openterms, setOpenterms] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const isSuperAdmin = selectorAuth?.user?.role === "super_admin";

  const superAdminMenuItems = [
    { path: "/admin/", label: "Home", icon: <MdHome /> },
    { path: "/admin/business", label: "Business", icon: <MdBusiness /> },
    { path: "/admin/categories", label: "Industries", icon: <MdCategory /> },
    { path: "/admin/module-settings", label: "Module Settings", icon: <MdCategory /> },
    { path: "/admin/subscription/billing", label: "Billing", icon: <MdPayment /> },
    { path: "/admin/coupons", label: "Discounts", icon: <MdDiscount /> },
    { path: "/admin/plans", label: "Plans", icon: <MdPayments /> },
    { path: "/admin/templates", label: "Messages", icon: <MdAbc /> },
  ];

  // Build business menu items dynamically based on tenant modules
  const buildBusinessMenuItems = () => {
    const items = [
      { path: "/admin/", label: "Home", icon: <MdHome />, module: null }, // Always show Home
    ];

    // If tenant is not loaded yet, show default menu
    if (!tenant || !tenant.modules) {
      return [
        { path: "/admin/", label: "Home", icon: <MdHome /> },
        { path: "/admin/staff", label: "Staff", icon: <MdPerson /> },
        { path: "/admin/patient", label: "Patients", icon: <MdPeople /> },
        { path: "/admin/appointment", label: "Appointments", icon: <MdCalendarMonth /> },
        { path: "/admin/products", label: "Products", icon: <MdInventory2 /> },
        { path: "/admin/report", label: "Reports", icon: <MdReport /> },
        { path: "/admin/billing", label: "Billing", icon: <MdPayment /> },
      ];
    }

    // Staff module
    if (tenant.modules?.staff?.enabled) {
      // Use tenant.labels directly, fallback to hook labels, then default
      const staffLabel = tenant.labels?.staff_plural || labels?.staff_plural || "Staff";
      items.push({
        path: "/admin/staff",
        label: staffLabel,
        icon: <MdPerson />,
        module: "staff",
      });
    }

    // Customers/Patients module
    if (tenant.modules?.customers?.enabled) {
      // Use tenant.labels directly, fallback to hook labels, then default
      const customersLabel = tenant.labels?.customers || labels?.customers || "Customers";
      items.push({
        path: "/admin/patient",
        label: customersLabel,
        icon: <MdPeople />,
        module: "customers",
      });
    }

    // Appointments module
    if (tenant.modules?.appointments?.enabled) {
      // Use tenant.labels directly, fallback to hook labels, then default
      const appointmentsLabel = tenant.labels?.appointments || labels?.appointments || "Appointments";
      items.push({
        path: "/admin/appointment",
        label: appointmentsLabel,
        icon: <MdCalendarMonth />,
        module: "appointments",
      });
    }

    // Products module
    if (tenant.modules?.products?.enabled) {
      items.push({
        path: "/admin/products",
        label: tenant.labels?.products || "Products",
        icon: <MdInventory2 />,
        module: "products",
      });
    }

    // Reports module
    if (tenant.modules?.reports?.enabled) {
      items.push({
        path: "/admin/report",
        label: tenant.labels?.reports || "Reports",
        icon: <MdReport />,
        module: "reports",
      });
    }

    // Billing module
    if (tenant.modules?.billing?.enabled) {
      items.push({
        path: "/admin/billing",
        label: tenant.labels?.billing || "Billing",
        icon: <MdPayment />,
        module: "billing",
      });
    }

    return items;
  };

  const businessMenuItems = buildBusinessMenuItems();

  const menuItems = isSuperAdmin ? superAdminMenuItems : businessMenuItems;

  // Debug: Log tenant and labels to help diagnose
  useEffect(() => {
    if (tenant) {
      console.log('TopBar - Tenant loaded:', {
        industry: tenant.industry || tenant.industry_key,
        industry_name: tenant.industry_name,
        labels: tenant.labels,
        modules: tenant.modules
      });
      console.log('TopBar - Labels from hook:', labels);
    }
  }, [tenant, labels]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };


  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <AnimatePresence>
        {open && <SharePopup closePopup={() => setOpen(false)} />}
        {openLeads && <LeadsComponent open={openLeads} setClose={() => setOpenLeads(false)} />}
        {openterms && <TermsConditions closePopup={() => setOpenterms(false)} />}
      </AnimatePresence>
      <SettingsSidebar open={openSettings} onClose={() => setOpenSettings(false)} />

      <AppBar
        position="fixed"
        component={motion.div}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)",
          backgroundSize: "400% 400%",
          animation: "gradientShift 15s ease infinite",
          backdropFilter: "blur(20px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
          zIndex: 1200,
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          "@keyframes gradientShift": {
            "0%": {
              backgroundPosition: "0% 50%",
            },
            "50%": {
              backgroundPosition: "100% 50%",
            },
            "100%": {
              backgroundPosition: "0% 50%",
            },
          },
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 3 }, minHeight: "56px !important", height: "56px" }}>
          {/* Logo */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, type: "spring" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mr: 4 }}>
              <img src={logo} alt="Logo" className="h-8" style={{ filter: "brightness(0) invert(1)" }} />
            </Box>
          </motion.div>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexGrow: 1,
              gap: 1,
              alignItems: "center",
            }}
          >
            {menuItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.08, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: isActive(item.path) ? "#ffffff" : "rgba(255, 255, 255, 0.9)",
                    backgroundColor: isActive(item.path)
                      ? "rgba(255, 255, 255, 0.25)"
                      : "rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    px: 2,
                    py: 0.75,
                    textTransform: "none",
                    fontWeight: isActive(item.path) ? 700 : 500,
                    fontSize: "0.875rem",
                    backdropFilter: "blur(10px)",
                    border: isActive(item.path) ? "1px solid rgba(255, 255, 255, 0.3)" : "1px solid rgba(255, 255, 255, 0.1)",
                    boxShadow: isActive(item.path) ? "0 4px 12px rgba(0, 0, 0, 0.15)" : "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      color: "#ffffff",
                      border: "1px solid rgba(255, 255, 255, 0.4)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  {item.label}
                </Button>
              </motion.div>
            ))}

            {/* Additional Actions for Business Users */}
            {!isSuperAdmin && (
              <>

                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.45 }}
                  whileHover={{ scale: 1.08, y: -2 }} 
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    onClick={() => setOpenSettings(true)}
                    startIcon={<MdOutlineMiscellaneousServices />}
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "12px",
                      px: 2,
                      py: 0.75,
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        color: "#ffffff",
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                      },
                    }}
                  >
                    Settings
                  </Button>
                </motion.div>
              </>
            )}
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.9 }}
            >
              <IconButton
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  "&:hover": { 
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Badge badgeContent={0} color="error">
                  <MdNotifications />
                </Badge>
              </IconButton>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  "&:hover": { 
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "rgba(255, 255, 255, 0.3)",
                    color: "#ffffff",
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    border: "2px solid rgba(255, 255, 255, 0.5)",
                  }}
                >
                  {selectorAuth?.user?.name?.charAt(0)?.toUpperCase() || "U"}
                </Avatar>
              </IconButton>
            </motion.div>

            {/* Mobile Menu Button */}
            <motion.div
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  display: { md: "none" }, 
                  color: "rgba(255, 255, 255, 0.9)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <MdMenu />
              </IconButton>
            </motion.div>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: "12px",
            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {selectorAuth?.user?.name || "User"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectorAuth?.user?.email || ""}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          component={Link}
          to={`/admin/profile/${selectorAuth?.user?.business_id}`}
          onClick={handleProfileMenuClose}
        >
          <ListItemIcon>
            <MdAccountCircle />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            logOut(dispatch);
            nav("/admin/login");
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <MdLogout sx={{ color: "error.main" }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>


      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            pt: 8,
          },
        }}
      >
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={handleDrawerToggle}
                selected={isActive(item.path)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "rgba(11, 70, 157, 0.1)",
                    color: "#0B469D",
                    "&:hover": {
                      backgroundColor: "rgba(11, 70, 157, 0.15)",
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive(item.path) ? "#0B469D" : "#666" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
          {!isSuperAdmin && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { setOpenLeads(true); handleDrawerToggle(); }}>
                  <ListItemIcon>
                    <MdLineAxis />
                  </ListItemIcon>
                  <ListItemText primary="Leads" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { setOpenSettings(true); handleDrawerToggle(); }}>
                  <ListItemIcon>
                    <MdOutlineMiscellaneousServices />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
};

// Share Popup Component
const SharePopup = ({ closePopup }) => {
  const selector = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  // Call hook unconditionally (hooks must be called in the same order every render)
  const labels = useLabels();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-screen z-30 text-black h-screen left-0 top-0 fixed bg-black/40 flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-[90%] lg:w-[60%] min-h-[60%] p-10 bg-white rounded-2xl relative flex items-center gap-10 flex-col shadow-2xl"
      >
        <IconButton
          onClick={closePopup}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#666",
          }}
        >
          <MdClose />
        </IconButton>
        <Typography variant="h4" className="font-bold mb-4">
          QR Code
        </Typography>
        <div className="w-full flex items-center justify-center flex-wrap-reverse gap-10">
          <div className="flex-1 min-w-full lg:min-w-[45%]">
            <Typography variant="h6" className="text-center mb-4">
              Share your QR code for booking a {labels.appointment || "Appointment"}
            </Typography>
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" className="text-gray-500">
                OR
              </Typography>
            </Divider>
            <div className="w-full flex items-center gap-4 justify-center">
              <Box
                sx={{
                  flex: 1,
                  maxWidth: "70%",
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  component="a"
                  href={`https://taa.dukaani.in/business/${selector?.user?.name
                    ?.trim()
                    .replaceAll(" ", "_")}/${selector?.user?.business_id}/book-appointment`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`https://taa.dukaani.in/business/${selector?.user?.name
                    ?.trim()
                    .replaceAll(" ", "_")}/${selector?.user?.business_id}/book-appointment`}
                </Typography>
              </Box>
              <RWebShare
                data={{
                  text: `Hello, Click this link for booking ${labels.appointment || "Appointment"}.`,
                  url: `https://taa.dukaani.in/business/${selector?.user?.name
                    ?.trim()
                    .replaceAll(" ", "_")}/${selector?.user?.business_id}/book-appointment`,
                  title: "Share link",
                }}
                disableNative={false}
              >
                <IconButton sx={{ color: "#0B469D" }}>
                  <MdShare />
                </IconButton>
              </RWebShare>
              <CopyToClipboard
                onCopy={() => successMessage(dispatch, "URL Copied")}
                text={`https://taa.dukaani.in/business/${selector?.user?.name
                  ?.trim()
                  .replaceAll(" ", "_")}/${selector?.user?.business_id}/book-appointment`}
              >
                <IconButton sx={{ color: "#0B469D" }}>
                  <MdCopyAll />
                </IconButton>
              </CopyToClipboard>
            </div>
          </div>
          <Box
            sx={{
              bgcolor: "#0B469D",
              borderRadius: "12px",
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <QRCode
                size={200}
                value={`https://taa.dukaani.in/business/${selector?.user?.name
                  ?.trim()
                  .replaceAll(" ", "_")}/${selector?.user?.business_id}/book-appointment`}
              />
            </Box>
          </Box>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Terms & Conditions Component
const TermsConditions = ({ closePopup }) => {
  const [activeTab, setActiveTab] = useState(0);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full z-30 text-black h-full left-0 top-0 fixed bg-black/40 flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-[90%] h-[80%] lg:w-[60%] min-h-[60%] p-10 bg-white rounded-2xl relative flex items-center justify-center gap-10 flex-col overflow-y-scroll shadow-2xl"
      >
        <IconButton
          onClick={closePopup}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#666",
            zIndex: 1,
          }}
        >
          <MdClose />
        </IconButton>
        <Tabs className="tabs h-[100%] w-full">
          <TabList className="flex items-center justify-center relative gap-10 pb-6">
            <Tab>
              <button
                onClick={() => setActiveTab(0)}
                className={`font-bold text-2xl cursor-pointer ${
                  activeTab === 0 ? "border-b-2 border-blue-600" : ""
                }`}
              >
                Privacy Policy
              </button>
            </Tab>
            <Tab>
              <button
                onClick={() => setActiveTab(1)}
                className={`font-bold text-2xl cursor-pointer ${
                  activeTab === 1 ? "border-b-2 border-blue-600" : ""
                }`}
              >
                Terms & Conditions
              </button>
            </Tab>
          </TabList>
          <TabPanel>
            <Typography variant="body1" className="mb-4">
              This Privacy Policy ("Policy") applies to the [The Appointment App] ("Application")
              owned and operated by [Digital Amigos], a company registered in India. This Policy describes
              how the Application collects, uses, and discloses personal information of its users ("Users").
            </Typography>
            <ol className="list-decimal list-inside mb-8 space-y-2">
              <li>
                <strong>Collection of Information:</strong> The Application may collect personal information from Users, including but not limited to name, email address, phone number, and location. The Application may also collect non-personal information, such as usage data and device information.
              </li>
              <li>
                <strong>Use of Information:</strong> The Application uses personal information to provide its services to Users, improve the Application, and communicate with Users. The Application may also use non-personal information for analytics and research purposes.
              </li>
              <li>
                <strong>Disclosure of Information:</strong> The Application does not disclose personal information to third parties, except as required by law or to provide its services to Users. The Application may disclose non-personal information to third parties for analytics and research purposes.
              </li>
              <li>
                <strong>Security:</strong> The Application uses industry-standard security measures to protect personal information from unauthorized access, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
              </li>
              <li>
                <strong>Cookies:</strong> The Application may use cookies or similar technologies to collect non-personal information and improve the user experience. Users can disable cookies in their browser settings, but this may limit their use of the Application.
              </li>
              <li>
                <strong>Third-Party Services:</strong> The Application may use third-party services, such as analytics and advertising providers, that may collect personal information from Users. Users should review the privacy policies of these third-party services.
              </li>
              <li>
                <strong>Children's Privacy:</strong> The Application is not intended for children under the age of 13. The Application does not knowingly collect personal information from children under the age of 13.
              </li>
              <li>
                <strong>Changes to Policy:</strong> The Application may update this Policy from time to time. Users will be notified of any changes by email or through the Application. The User's continued use of the Application following any changes constitutes acceptance of the new Policy.
              </li>
              <li>
                <strong>Contact Us:</strong> Users may contact the Application at any time with questions or concerns about this Policy or the Application's privacy practices.
              </li>
              <li>
                <strong>Governing Law:</strong> This Policy shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with this Policy shall be resolved in accordance with the dispute resolution process outlined in the Terms and Conditions.
              </li>
              <li>
                <strong>Phonebook Access:</strong> Clearly state that the appointment application may request access to a user's phonebook contacts in order to facilitate appointment scheduling, or any other related activities and explain how this data will be used, such as to retrieve contact names and phone numbers for appointment reminders or notifications.
              </li>
              <li className="pb-10">
                <strong>Collection and Use of Location Data:</strong> The application may collect and use your location data when you grant access to your device's location services at the time of login. This location data may be used to verify your geographical location for login authentication purposes or to provide personalized content based on your location. The location data collected may include your device's GPS coordinates, Wi-Fi network information, cellular network information, or IP address.
              </li>
            </ol>
          </TabPanel>
          <TabPanel>
            <Typography variant="body1" className="mb-4">
              These Terms and Conditions ("Agreement") govern the use of the [The Appointment App]
              ("Application"), which is owned and operated by [Digital Amigos], a company registered in India.
              By using this Application, you ("User") agree to be bound by these terms and conditions.
            </Typography>
            <ol className="list-decimal list-inside mb-8 space-y-2">
              <li>
                <strong>Acceptance of Terms:</strong> By accessing or using the Application, you agree to be bound by the terms and conditions of this Agreement. If you do not agree to these terms, you may not use the Application.
              </li>
              <li>
                <strong>Use of Application:</strong> The User acknowledges and agrees that the Application is intended for personal use only and is not to be used for commercial purposes. The User agrees to use the Application in a lawful and responsible manner, and to comply with all applicable laws and regulations.
              </li>
              <li>
                <strong>Intellectual Property Rights:</strong> The User acknowledges and agrees that all content and materials on the Application, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are protected by copyright, trademark, or other proprietary rights and laws.
              </li>
              <li>
                <strong>Privacy Policy:</strong> The User agrees to the Application's Privacy Policy, which is incorporated herein by reference. The Privacy Policy sets out the User's rights and obligations with respect to the collection, use, and disclosure of personal information.
              </li>
              <li>
                <strong>User Conduct:</strong> The User agrees not to use the Application in any manner that is illegal, harmful, or interferes with the use of the Application by others. The User agrees not to transmit any content that is obscene, defamatory, or infringes upon the rights of others.
              </li>
              <li>
                <strong>Termination:</strong> The Company reserves the right to terminate the User's use of the Application at any time without notice or liability, for any reason including, but not limited to, violation of these terms and conditions.
              </li>
              <li>
                <strong>Disclaimer of Warranties:</strong> The Application is provided on an "as is" basis, without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </li>
              <li>
                <strong>Limitation of Liability:</strong> In no event shall the Company or its affiliates be liable to the User or any third party for any damages, including without limitation, direct, indirect, incidental, consequential, special, or punitive damages, arising out of or in connection with the use of the Application.
              </li>
              <li>
                <strong>Indemnification:</strong> The User agrees to indemnify and hold the Company and its affiliates, officers, directors, and employees harmless from any claims, damages, or expenses, including attorney's fees, arising out of or in connection with the User's use of the Application.
              </li>
              <li>
                <strong>Governing Law and Jurisdiction:</strong> This Agreement shall be governed by and construed in accordance with the laws of India. The User agrees that any legal action arising out of or in connection with this Agreement shall be brought in the courts of Indore, India.
              </li>
              <li>
                <strong>Changes to Terms and Conditions:</strong> The Company reserves the right to change these terms and conditions at any time without notice. The User's continued use of the Application following any changes constitutes acceptance of the new terms and conditions.
              </li>
              <li>
                <strong>Entire Agreement:</strong> This Agreement constitutes the entire agreement between the User and the Company with respect to the use of the Application and supersedes all prior or contemporaneous communications and proposals, whether oral or written, between the User and the Company.
              </li>
              <li>
                <strong>Data Usage:</strong> Specify that the phonebook contact information accessed by the appointment application will only be used for the purpose of scheduling appointments or for any app related purposes and will not be shared with third parties or used for any other purposes without the user's explicit consent.
              </li>
              <li>
                <strong>Location Access:</strong> By using the application and granting access to your location, you acknowledge and agree that the application may collect and use your location data for the purpose of providing location-based features, such as identifying your geographical location for login authentication or providing personalized content based on your location.
              </li>
              <li>
                <strong>Cancellation:</strong> Please note that all purchases are final, and we do not offer cancellations or refunds once a plan is purchased. We strongly recommend carefully evaluating your decision before proceeding with the purchase. If you have any concerns or questions about the plan, please contact our customer support team prior to making the purchase. Your understanding regarding our cancellation policy is greatly appreciated, and we are committed to providing exceptional service.
              </li>
              <li>
                <strong>Refund Policy:</strong> Please note that all purchases are non-refundable. We recommend carefully considering your needs before making a purchase. We strive to provide comprehensive information to assist in your decision-making process. If you have any inquiries or concerns, please contact our customer support team prior to completing your purchase. We appreciate your understanding of our refund policy as we aim to deliver exceptional products and services to ensure a positive customer experience.
              </li>
              <li>
                <strong>Return Policy:</strong> Due to the nature of our intangible and digital services, we do not have a return policy in place. Our team is dedicated to providing high-quality digital solutions and exceptional customer service. If you have any questions or concerns about our services, please contact our customer support team for assistance. We appreciate your understanding and trust in our services. Thank you for choosing our company.
              </li>
              <li className="pb-10">
                <strong>Payment Policy:</strong> Our payment terms and conditions include accepting payments through various methods, payment due dates from the day after the current subscription period ends, customers being responsible for applicable taxes and fees, payments made in Indian currency, prompt payment for uninterrupted service, and resolving disputes through our customer support team. We treat payment-related information with utmost confidentiality and comply with applicable laws and regulations.
              </li>
            </ol>
          </TabPanel>
        </Tabs>
      </motion.div>
    </motion.div>
  );
};

export default TopBar;

